import React, { useState, useEffect } from 'react';
import { Users, Send, X, Mail, UserCheck } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BulkMessageComposer = ({ user, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [formData, setFormData] = useState({
    recipient_type: 'individual_users',
    recipient_ids: [],
    team_id: '',
    school_id: '',
    subject: '',
    content: '',
    message_type: 'text'
  });
  const [availableRecipients, setAvailableRecipients] = useState([]);
  const [teams, setTeams] = useState([]);
  const { toast } = useToast();

  useEffect(() => {
    fetchTeams();
    fetchAvailableRecipients();
  }, [formData.recipient_type, formData.team_id, formData.school_id]);

  const fetchTeams = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/teams`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTeams(data);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const fetchAvailableRecipients = async () => {
    if (!formData.recipient_type) return;

    setRecipientsLoading(true);
    try {
      const params = new URLSearchParams({
        recipient_type: formData.recipient_type
      });
      
      if (formData.team_id) params.append('team_id', formData.team_id);
      if (formData.school_id) params.append('school_id', formData.school_id);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/messages/available-recipients?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableRecipients(data.recipients);
      } else {
        setAvailableRecipients([]);
      }
    } catch (error) {
      console.error('Error fetching recipients:', error);
      setAvailableRecipients([]);
    } finally {
      setRecipientsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.subject.trim() || !formData.content.trim()) {
      toast({
        title: 'Error',
        description: 'Please fill in both subject and message content',
        variant: 'destructive'
      });
      return;
    }

    if (formData.recipient_type === 'individual_users' && formData.recipient_ids.length === 0) {
      toast({
        title: 'Error',
        description: 'Please select at least one recipient',
        variant: 'destructive'
      });
      return;
    }

    if (formData.recipient_type === 'team_players' && !formData.team_id) {
      toast({
        title: 'Error',
        description: 'Please select a team',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/messages/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const result = await response.json();
        
        toast({
          title: 'Messages Sent Successfully',
          description: `Sent to ${result.sent_count} out of ${result.total_recipients} recipients`
        });
        
        onClose && onClose();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send messages');
      }
    } catch (error) {
      console.error('Error sending bulk message:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to send messages. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRecipientTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      recipient_type: type,
      recipient_ids: [],
      team_id: '',
      school_id: ''
    }));
  };

  const handleRecipientToggle = (recipientId) => {
    setFormData(prev => ({
      ...prev,
      recipient_ids: prev.recipient_ids.includes(recipientId)
        ? prev.recipient_ids.filter(id => id !== recipientId)
        : [...prev.recipient_ids, recipientId]
    }));
  };

  const getRecipientTypeOptions = () => {
    const options = [];
    
    if (user.role === 'system-admin') {
      options.push(
        { value: 'individual_users', label: 'Individual Users', description: 'Select specific users to message' },
        { value: 'team_players', label: 'Team Players', description: 'Message all players in a team' },
        { value: 'all_coaches', label: 'All Coaches', description: 'Message all coaches in the system' },
        { value: 'all_teams', label: 'All Athletes', description: 'Message all athletes across all teams' }
      );
    } else if (user.role === 'sport-performance-consultant') {
      options.push(
        { value: 'individual_users', label: 'Individual Users', description: 'Message coaches, admins, or athletes' },
        { value: 'team_players', label: 'Team Players', description: 'Message all players in a team' },
        { value: 'all_teams', label: 'All Athletes', description: 'Message all athletes' }
      );
    } else if (user.role === 'coach' || user.role === 'team-admin') {
      options.push(
        { value: 'team_players', label: 'Team Players', description: 'Message players in your teams' },
        { value: 'individual_users', label: 'Individual Athletes', description: 'Select specific athletes' }
      );
    } else if (user.role === 'school-admin') {
      options.push(
        { value: 'all_coaches', label: 'All Coaches', description: 'Message all coaches in your school' },
        { value: 'individual_users', label: 'Individual Users', description: 'Message specific coaches or staff' }
      );
    }
    
    return options;
  };

  const recipientTypeOptions = getRecipientTypeOptions();

  if (!user || recipientTypeOptions.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 text-center">
          <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">You don't have permission to send bulk messages.</p>
          <button
            onClick={onClose}
            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
                <Users className="w-5 h-5 text-emerald-600" />
                <span>Send Bulk Message</span>
              </h2>
              <p className="text-gray-600 text-sm mt-1">
                Send messages to multiple recipients based on your role permissions
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={loading}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Recipient Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Recipient Type
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {recipientTypeOptions.map((option) => (
                  <label
                    key={option.value}
                    className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                      formData.recipient_type === option.value
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="recipient_type"
                      value={option.value}
                      checked={formData.recipient_type === option.value}
                      onChange={(e) => handleRecipientTypeChange(e.target.value)}
                      className="sr-only"
                    />
                    <div className="font-medium text-gray-900">{option.label}</div>
                    <div className="text-sm text-gray-600">{option.description}</div>
                  </label>
                ))}
              </div>
            </div>

            {/* Team Selection for team_players */}
            {formData.recipient_type === 'team_players' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Team
                </label>
                <select
                  value={formData.team_id}
                  onChange={(e) => setFormData(prev => ({...prev, team_id: e.target.value}))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  required
                  disabled={loading}
                >
                  <option value="">Select a team...</option>
                  {teams.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name} ({team.athlete_ids?.length || 0} athletes)
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Individual Recipient Selection */}
            {formData.recipient_type === 'individual_users' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Recipients ({formData.recipient_ids.length} selected)
                </label>
                <div className="border border-gray-300 rounded-md max-h-60 overflow-y-auto">
                  {recipientsLoading ? (
                    <div className="p-4 text-center text-gray-500">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600 mx-auto"></div>
                      <p className="mt-2">Loading recipients...</p>
                    </div>
                  ) : availableRecipients.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      No recipients available
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {availableRecipients.map((recipient) => (
                        <label
                          key={recipient.id}
                          className="flex items-center space-x-3 p-3 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={formData.recipient_ids.includes(recipient.id)}
                            onChange={() => handleRecipientToggle(recipient.id)}
                            className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{recipient.name}</div>
                            <div className="text-sm text-gray-500">{recipient.email} • {recipient.role}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Recipient Count Display */}
            {formData.recipient_type !== 'individual_users' && availableRecipients.length > 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 text-emerald-800">
                  <UserCheck className="w-4 h-4" />
                  <span className="font-medium">
                    {availableRecipients.length} recipients will receive this message
                  </span>
                </div>
              </div>
            )}

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject *
              </label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData(prev => ({...prev, subject: e.target.value}))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="Enter message subject"
                required
                disabled={loading}
              />
            </div>

            {/* Message Content */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message Content *
              </label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData(prev => ({...prev, content: e.target.value}))}
                rows={6}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="Enter your message content"
                required
                disabled={loading}
              />
            </div>

            {/* Submit Buttons */}
            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-4 rounded-md transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-2 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                disabled={loading}
              >
                {loading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                <Send className="w-4 h-4" />
                <span>{loading ? 'Sending...' : 'Send Messages'}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BulkMessageComposer;